import './styles.scss';
import '@xyflow/react/dist/style.css';

import { useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
    ReactFlow,
    Background,
    Controls,
    BackgroundVariant,
    useReactFlow,
} from '@xyflow/react';

import useFormBuilder from '../hooks/useFormBuilder';
import FormBuilderHeader from './components/Header';
import StepNode from './components/StepNode';
import AddBlockNode from './components/AddBlockNode';
import RightPanel from './components/RightPanel';

// Must be stable (outside component) — React Flow re-creates internal state on nodeTypes changes
const nodeTypes = {
    stepNode: StepNode,
    addBlockNode: AddBlockNode,
};

const SPRING = { type: 'spring', damping: 38, stiffness: 420 };

function AutoFitOnAdd({ nodes }) {
    const { setCenter, getViewport, getNode } = useReactFlow();
    const prevLength = useRef(nodes.length);

    useEffect(() => {
        if (nodes.length <= prevLength.current) {
            prevLength.current = nodes.length;
            return;
        }
        prevLength.current = nodes.length;

        // New step node is second-to-last; last is always __add-block__
        const newNode = nodes.at(-2);
        if (!newNode || newNode.type !== 'stepNode') return;

        setTimeout(() => {
            const measured = getNode(newNode.id);
            const { zoom } = getViewport();
            const w = measured?.measured?.width ?? 220;
            const h = measured?.measured?.height ?? 80;
            setCenter(
                newNode.position.x + w / 2,
                newNode.position.y + h / 2,
                { zoom, duration: 400 },
            );
        }, 50);
    }, [nodes.length]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
}

export default function FormBuilderModalView({ open, onClose, onSaveSuccess, communityId, editFormId, templateData }) {
    const {
        formName, setFormName,
        formActive, setFormActive,
        redirectChoice, setRedirectChoice,
        returnUrl, setReturnUrl,
        allowMultipleAnswers, setAllowMultipleAnswers,
        banner, setBanner,
        theme, setTheme,
        actions, addAction, updateAction, removeAction,
        nodes: rawNodes,
        edges,
        steps,
        selectedStepId, selectedStep,
        selectStep, addStep, updateStep, deleteStep,
        addQuestion, updateQuestion, removeQuestion, reorderQuestions,
        saving, handleSave, handleActivate,
        errors, clearError,
        scrollSignal,
        loading,
    } = useFormBuilder({ communityId, onSaveSuccess, editFormId, templateData });

    // Inject display-only data into nodes (no callbacks — handled via onNodeClick)
    const nodes = useMemo(() => rawNodes.map(node => {
        if (node.type === 'stepNode') {
            const stepIndex = steps.findIndex(s => s.id === node.id);
            return {
                ...node,
                data: {
                    ...node.data,
                    stepNumber: stepIndex + 1,
                    isSelected: node.id === selectedStepId,
                },
            };
        }
        return node;
    }), [rawNodes, selectedStepId, steps]);

    // ── Single authoritative click handler for all nodes ──────────────────
    // React Flow guarantees onNodeClick fires regardless of inner event handling.
    const handleNodeClick = useCallback((event, node) => {
        if (node.type === 'addBlockNode') {
            addStep();
            return;
        }
        if (node.type === 'stepNode') {
            // Detect delete button via data attribute (avoids stopPropagation issues)
            if (event.target.closest('[data-action="delete"]')) {
                deleteStep(node.id);
                return;
            }
            selectStep(node.id);
        }
    }, [addStep, deleteStep, selectStep]);

    const handlePaneClick = useCallback(() => selectStep(null), [selectStep]);

    const selectedStepIndex = steps.findIndex(s => s.id === selectedStepId);
    const selectedStepNumber = selectedStepIndex >= 0 ? selectedStepIndex + 1 : null;

    const modal = (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        className="form-builder-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={onClose}
                    />

                    <motion.div
                        className="form-builder-modal"
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={SPRING}
                    >
                        <FormBuilderHeader
                            formName={formName}
                            setFormName={setFormName}
                            formActive={formActive}
                            setFormActive={setFormActive}
                            saving={saving}
                            onSave={handleSave}
                            onActivate={handleActivate}
                            onClose={onClose}
                        />

                        {loading ? (
                            <div className="fbm-loading">
                                <div className="fbm-spinner" />
                            </div>
                        ) : (
                            <div className="fbm-body">
                                <div className="fbm-canvas">
                                    <ReactFlow
                                        nodes={nodes}
                                        edges={edges}
                                        nodeTypes={nodeTypes}
                                        fitView
                                        fitViewOptions={{ padding: 0.6, maxZoom: 1.2 }}
                                        minZoom={0.3}
                                        maxZoom={2}
                                        onNodeClick={handleNodeClick}
                                        onPaneClick={handlePaneClick}
                                        nodesDraggable={false}
                                        nodesConnectable={false}
                                        deleteKeyCode={null}
                                    >
                                        <Background
                                            variant={BackgroundVariant.Dots}
                                            gap={24}
                                            size={1.2}
                                            color="var(--surface-8)"
                                        />
                                        <Controls
                                            showInteractive={false}
                                            style={{
                                                bottom: 'var(--gap-2)',
                                                left: 'var(--gap-2)',
                                            }}
                                        />
                                        <AutoFitOnAdd nodes={nodes} />
                                    </ReactFlow>
                                </div>

                                <RightPanel
                                    selectedStep={selectedStep}
                                    stepNumber={selectedStepNumber}
                                    updateStep={updateStep}
                                    addQuestion={addQuestion}
                                    updateQuestion={updateQuestion}
                                    removeQuestion={removeQuestion}
                                    reorderQuestions={reorderQuestions}
                                    onDeselect={() => selectStep(null)}
                                    communityId={communityId}
                                    formName={formName}
                                    setFormName={setFormName}
                                    formActive={formActive}
                                    setFormActive={setFormActive}
                                    redirectChoice={redirectChoice}
                                    setRedirectChoice={setRedirectChoice}
                                    returnUrl={returnUrl}
                                    setReturnUrl={setReturnUrl}
                                    allowMultipleAnswers={allowMultipleAnswers}
                                    setAllowMultipleAnswers={setAllowMultipleAnswers}
                                    banner={banner}
                                    setBanner={setBanner}
                                    theme={theme}
                                    setTheme={setTheme}
                                    actions={actions}
                                    addAction={addAction}
                                    updateAction={updateAction}
                                    removeAction={removeAction}
                                    errors={errors}
                                    clearError={clearError}
                                    scrollSignal={scrollSignal}
                                />
                            </div>
                        )}
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );

    return createPortal(modal, document.body);
}
